import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by smoothly morphing the current media gallery.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Create progressive left-to-right image transition
    this.#createProgressiveTransition(newMediaGallery);
  };

  /**
   * Creates a progressive transition for image changes based on admin settings
   * @param {HTMLElement} newMediaGallery - The new media gallery element
   */
  #createProgressiveTransition(newMediaGallery) {
    const currentImages = this.querySelectorAll('.product-media__image');
    const newImages = newMediaGallery.querySelectorAll('.product-media__image');
    
    // If images are the same, skip transition
    if (currentImages.length && newImages.length && 
        currentImages[0]?.src === newImages[0]?.src) {
      return;
    }

    // Check if progressive transitions are enabled
    const progressiveEnabled = this.dataset.enableProgressiveTransition === 'true';
    
    if (!progressiveEnabled) {
      // Use simple morph without progressive effect (original behavior)
      morph(this, newMediaGallery);
      return;
    }

    // Get transition settings
    const direction = this.dataset.transitionDirection || 'left-to-right';
    const duration = parseInt(this.dataset.transitionDuration) || 650;

    // Add transition classes based on settings
    this.classList.add('media-gallery--transitioning');
    this.classList.add(`media-gallery--transitioning-${direction}`);
    
    // Set dynamic CSS custom property for duration
    this.style.setProperty('--transition-duration', `${duration}ms`);
    
    // Start the morph process
    morph(this, newMediaGallery);
    
    // Reset transition state after animation completes
    setTimeout(() => {
      this.classList.remove('media-gallery--transitioning');
      this.classList.remove(`media-gallery--transitioning-${direction}`);
      this.style.removeProperty('--transition-duration');
    }, duration + 50); // Animation duration + buffer
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
